import { RiFolderVideoLine } from "react-icons/ri";

import { ArchivedIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/groupsAndContent/ArchivedIcon";
import { GroupsIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/groupsAndContent/GroupsIcon";
import { NexusIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/groupsAndContent/NexusIcon";
import { PublishedIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/groupsAndContent/PublishedIcon";
import { QuestionBankIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/groupsAndContent/QuestionBankIcon";
import { TrailsIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/groupsAndContent/TrailsIcon";
import { IOptionsHook, ISubSection } from "pages/[tenant]/console/types";

import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useFeature } from "shared/utils/hooks/useFeature";
import isVideosDiscontinued from "shared/utils/isVideosDiscontinued";

import CommentsIcon from "@Console/assets/icons/CommentsIcon";
import SpreadsheetIcon from "@Console/assets/icons/SpreadsheetIcon";
import { DefinitionIcon } from "@Console/assets/icons/sidebarSection/gamification/DefinitionIcon";
import { LevelsIcon } from "@Console/assets/icons/sidebarSection/gamification/LevelsIcon";
import { AnamneseIcon } from "@Console/assets/icons/sidebarSection/groupsAndContent/AnamneseIcon";
import { AnamneseQuestionIcon } from "@Console/assets/icons/sidebarSection/groupsAndContent/AnamneseQuestionIcon";
import { RewardsIcon } from "@Console/assets/icons/sidebarSection/groupsAndContent/RewardsIcon";
import { TriggersIcon } from "@Console/assets/icons/sidebarSection/groupsAndContent/TriggersIcon";
import { BagIcon } from "@Console/assets/icons/sidebarSection/sales/BagIcon";

export const useGroupAndContentOptions = (
  amountOfUnrevisedTasks: number,
): IOptionsHook => {
  const isTrailEnabled = useFeature("f_trails");

  const isVideosEnabledByFeatureFlag = useFeature("f_upload_ensinio_video");
  const isLivesEnabled = useFeature("f_lives");

  const isVideosDisabledByDate = isVideosDiscontinued();

  const isVideosEnabled =
    isVideosDisabledByDate || isVideosEnabledByFeatureFlag;

  const isVideosAndLivesEnabled = isVideosEnabled && isLivesEnabled;

  const videosAndLivesBadge = isVideosAndLivesEnabled
    ? "Vídeos e Lives"
    : "Vídeos";

  const isCommentsEnabled = useFeature("f_comments_management");
  const isAssignmentsEnabled = useFeature("f_assignments_management");
  const isGlobalGamificationEnabled = useFeature("f_global_gamification");
  const isAnamneseEnabled = useFeature("f_anamnese");
  const isStoreEnabled = useFeature("f_rewards_shop");
  const isLevelsEnabled = useFeature("f_levels");

  const { loggedUser, rolesUser } = useAuthContext();

  const hasPermissionViewComments = [
    "visualizar-postagens-e-comentarios-publicos",
    "visualizar-comentarios-privados",
  ].some((permission) => rolesUser?.includes(permission));

  const hasPermission = (permissionsList: string[]): boolean =>
    rolesUser?.some((permission) => permissionsList?.includes(permission));

  const isAdmin =
    loggedUser?.admin ||
    loggedUser?.superadmin ||
    loggedUser?.console_role?.slug === "superadmin" ||
    loggedUser?.console_role?.slug === "admin";

  const hasPermissionAdminConsole = hasPermission(["acessar-admin-console"]);

  const isInLocalhost =
    typeof window !== "undefined" &&
    window?.location?.href?.includes("localhost");

  const sectionLabel = "Grupos e recursos";
  const groupsAndContentSubSections: ISubSection[] = [
    {
      label: "Grupos",
      options: [
        {
          id: 1,
          label: "Todos os grupos",
          description:
            "Os grupos permitem que você reúna seus usuários, conteúdos, eventos e comunidade em um só lugar.",
          path: "/console/groups",
          icon: <GroupsIcon />,
          shouldHide: !hasPermissionAdminConsole,
        },
        {
          id: 2,
          label: "Publicados",
          path: "/console/groups/published",
          icon: <PublishedIcon />,
          shouldHide: true,
        },
        {
          id: 3,
          label: "Arquivados",
          path: "/console/groups/archived",
          icon: <ArchivedIcon />,
          shouldHide: true,
        },
      ],
    },
    {
      label: "Recursos",
      options: [
        {
          id: 20,
          label: "Vínculos",
          description:
            "Relacione grupos e permita que seus alunos obtenham acesso a outros grupos a partir da mesma inscrição.",
          path: "/console/groups/bindings",
          icon: <NexusIcon />,
          shouldHide: !isInLocalhost || !isAdmin,
        },
        {
          id: 21,
          label: "Banco de Questões",
          description:
            "Reutilize questões entre quizzes de vários grupos diferentes e mantenha uma relação centralizada.",
          path: "/console/questionbank",
          icon: <QuestionBankIcon />,
          shouldHide: true,
        },
        {
          id: 22,
          label: "Trilhas",
          description:
            "As trilhas permitem que você monte uma escada de valor multi-nível e determine uma direção para que seus usuários completem o conteúdo.",
          path: "/console/trails",
          icon: <TrailsIcon />,
          shouldHide: !isTrailEnabled || !hasPermissionAdminConsole,
        },
        {
          id: 22,
          label: videosAndLivesBadge,
          description: "",
          path: "/console/videos",
          icon: <RiFolderVideoLine />,
          shouldHide: !isVideosEnabled || !hasPermissionAdminConsole,
        },
      ],
    },
    {
      label: "Anamneses",
      options: [
        {
          id: 50,
          label: "Questionários",
          icon: <AnamneseQuestionIcon />,
          path: "/console/anamnese/questions",
          shouldHide: !isAnamneseEnabled || !hasPermissionAdminConsole,
        },
        {
          id: 51,
          label: "Anamneses",
          icon: <AnamneseIcon />,
          path: "/console/anamnese/answers",
          shouldHide: !isAnamneseEnabled || !hasPermissionAdminConsole,
        },
      ],
    },
    {
      label: "Moderação",
      options: [
        {
          id: 30,
          label: "Comentários",
          description:
            "Acompanhe, responda e revise todos os comentários da sua plataforma em um único lugar.",
          path: "/console/comments",
          icon: <CommentsIcon />,
          shouldHide: !isCommentsEnabled || !hasPermissionViewComments,
        },
        {
          id: 31,
          label: "Tarefas",
          description:
            "Acompanhe e revise todas as atividades da sua plataforma em um único lugar.",
          path: "/console/tasks",
          icon: <SpreadsheetIcon />,
          shouldHide: !isAssignmentsEnabled || !hasPermissionAdminConsole,
          notificationNumber:
            amountOfUnrevisedTasks > 0 ? amountOfUnrevisedTasks : null,
        },
      ],
    },
    {
      label: "Gamificação",
      options: [
        {
          id: 40,
          label: "Definições",
          description: "",
          path: "/console/definitions",
          icon: <DefinitionIcon />,
          shouldHide:
            !isGlobalGamificationEnabled || !hasPermissionAdminConsole,
        },
        {
          id: 41,
          label: "Gatilhos",
          description: "",
          path: "/console/triggers",
          icon: <TriggersIcon />,
          shouldHide:
            !isGlobalGamificationEnabled || !hasPermissionAdminConsole,
        },
        {
          id: 42,
          label: "Níveis",
          description: "",
          path: "/console/levels",
          icon: <LevelsIcon />,
          shouldHide:
            !isGlobalGamificationEnabled ||
            !isLevelsEnabled ||
            !hasPermissionAdminConsole,
        },
        {
          id: 43,
          label: "Recompensas",
          description: "",
          path: "/console/rewards",
          icon: <RewardsIcon />,
          shouldHide: !isStoreEnabled || !hasPermissionAdminConsole,
        },
        {
          id: 44,
          label: "Resgate de itens",
          description: "",
          path: "/console/rescue",
          icon: <BagIcon />,
          shouldHide: !isStoreEnabled || !hasPermissionAdminConsole,
        },
      ],
    },
  ];

  return {
    subSections: groupsAndContentSubSections,
    label: sectionLabel,
  };
};
